/**
 * Represents the CricketTeamPortfolio component, which displays a detailed view of a cricket team's players.
 * 
 * This component provides:
 * - A searchable and filterable grid of team members
 * - Ability to view player details in a modal
 * - Tabs for viewing players by different categories
 * 
 * @component
 * @returns {React.ReactElement} A comprehensive team portfolio interface for a cricket team
 */
import React, { useState, useEffect, Fragment } from 'react';
import { Search, X, ChevronDown, Filter, User, Users, Award } from 'lucide-react';
import { Dialog, Transition } from '@headlessui/react';

// Define types for player data
interface Player {
  id: number;
  name: string;
  role: 'Batsman' | 'Bowler' | 'All-rounder' | 'Wicket-keeper';
  specialization: string;
  stats: string;
  image: string;
  country: string;
  jersey: number;
}

const CricketTeamPortfolio = () => {
  // Team data with real player information
  const teamMembers: Player[] = [
    {
      id: 1,
      name: "Swapnali",
      role: "All-rounder",
      specialization: "Captain",
      stats: "Team leader and skilled all-rounder",
      image: "/images/players/Swapnali.jpeg", // Replace with actual image path when deploying
      country: "India",
      jersey: 1
    },
    {
      id: 2,
      name: "Manasa",
      role: "All-rounder",
      specialization: "Batter/Bowler",
      stats: "Balanced player with all-round skills",
      image: "/images/players/Manasa.jpeg", 
      country: "India",
      jersey: 2
    },
    {
      id: 3,
      name: "Brahmini",
      role: "All-rounder",
      specialization: "Batter/Bowler",
      stats: "Versatile all-round performer",
      image: "/images/players/Brahmini.jpeg", 
      country: "India",
      jersey: 3
    },
    {
      id: 4,
      name: "Bavya",
      role: "Wicket-keeper",
      specialization: "Wicket-keeper/Batter/Bowler",
      stats: "Multi-talented player with keeping skills",
      image: "/images/players/Bavya.jpeg", 
      country: "India",
      jersey: 4
    },
    {
      id: 5,
      name: "Harshada",
      role: "Bowler",
      specialization: "Bowling specialist",
      stats: "Key bowling unit member",
      image: "/images/players/Harshada.jpeg", 
      country: "India",
      jersey: 5
    },
    {
      id: 6,
      name: "Varsha",
      role: "All-rounder",
      specialization: "Bowler/Batter",
      stats: "Bowling all-rounder with batting ability",
      image: "/images/players/Varsha.jpeg", 
      country: "India",
      jersey: 6
    },
    {
      id: 7,
      name: "Haritha",
      role: "Wicket-keeper",
      specialization: "Wicket-keeper/Batter/Bowler",
      stats: "Complete player with keeping skills",
      image: "/images/players/Haritha.jpeg", 
      country: "India",
      jersey: 7
    },
    {
      id: 8,
      name: "Deepti",
      role: "All-rounder",
      specialization: "Batter/Bowler",
      stats: "Reliable all-rounder",
      image: "/images/players/Deepti.jpeg", 
      country: "India",
      jersey: 8
    },
    {
      id: 9,
      name: "Neha D",
      role: "Batsman",
      specialization: "Batting specialist",
      stats: "Strong middle-order batter",
      image: "/images/players/NehaD.jpeg", 
      country: "India",
      jersey: 9
    },
    {
      id: 10,
      name: "Anita",
      role: "Batsman",
      specialization: "Batting specialist",
      stats: "Dependable batter",
      image: "/images/players/Anita.jpeg", 
      country: "India",
      jersey: 10
    },
    {
      id: 11,
      name: "Shravya",
      role: "Batsman",
      specialization: "Batting specialist",
      stats: "Focused on batting",
      image: "/images/players/Shravya.jpeg", 
      country: "India",
      jersey: 11
    },
    {
      id: 12,
      name: "Anupriya",
      role: "All-rounder",
      specialization: "Batter/Bowler",
      stats: "Versatile all-round player",
      image: "/images/players/Anupriya.jpeg", 
      country: "India",
      jersey: 12
    }
  ];

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    if (selectedPlayer) {
      setIsModalOpen(true);
    }
  }, [selectedPlayer]);

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedPlayer(null), 200);
  };

  // Filter players based on search term and role filter
  const filteredTeamMembers = teamMembers.filter(member => {
    const matchesSearch = searchTerm === '' || 
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      member.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.specialization.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = selectedFilter === 'all' || member.role.toLowerCase() === selectedFilter.toLowerCase();
    
    return matchesSearch && matchesFilter;
  });

  // Helper function to get badge color based on role
  const getRoleBadgeColor = (role: Player['role']): string => {
    switch(role) {
      case 'Batsman': return 'bg-emerald-500';
      case 'Bowler': return 'bg-amber-500';
      case 'All-rounder': return 'bg-violet-500';
      case 'Wicket-keeper': return 'bg-yellow-400';
      default: return 'bg-violet-800';
    }
  };

  // Helper function to get role icon
  const getRoleIcon = (role: Player['role']) => {
    switch(role) {
      case 'Batsman': return <User className="mr-1 h-4 w-4" />;
      case 'Bowler': return <Award className="mr-1 h-4 w-4" />;
      case 'All-rounder': return <Users className="mr-1 h-4 w-4" />;
      case 'Wicket-keeper': return <Award className="mr-1 h-4 w-4" />;
      default: return <User className="mr-1 h-4 w-4" />;
    }
  };

  return (
    <div className="bg-gradient-to-b from-yellow-50 to-amber-50 min-h-screen">
      {/* Header with MOMENTUM Logo */}
      <div className="bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col items-center justify-center">
            {/* MOMENTUM Logo */}
            <div className="w-24 h-24 mb-3 relative">
              <div className="relative w-full h-full">
                <img 
                  src="/images/momentum-logo.jpeg" 
                  alt="Momentum Logo" 
                  className="w-full h-full object-cover rounded-full shadow-lg ring-4 ring-white" 
                />
                {/* Add glossy overlay effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent rounded-full"></div>
              </div>
            </div>
            
            <h1 className="text-3xl font-extrabold text-violet-900 mb-1 text-center flex items-center">
              <span className="mr-2">MOMENTUM</span>
              <span className="bg-violet-900 text-white text-lg px-3 py-1 rounded-full">Cricket</span>
            </h1>
            <p className="text-violet-900/80 font-medium">Team Squad 2025</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto p-4 max-w-7xl">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl p-5 mb-6 shadow-md">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-violet-900">Team Squad</h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center text-violet-700 text-sm">
                  <Award className="h-4 w-4 mr-1" />
                  <span>Captain: Swapnali</span>
                </div>
                <span className="text-gray-300">|</span>
                <div className="flex items-center text-violet-700 text-sm">
                  <Users className="h-4 w-4 mr-1" />
                  <span>Players: 12</span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              {/* Search */}
              <div className="relative">
                <input 
                  type="text"
                  placeholder="Search players..." 
                  className="w-full min-w-64 bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-10 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              
              {/* Filter Dropdown */}
              <div className="relative">
                <div className="inline-flex items-center">
                  <div className="relative">
                    <select
                      className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-10 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                      value={selectedFilter}
                      onChange={(e) => setSelectedFilter(e.target.value)}
                    >
                      <option value="all">All Roles</option>
                      <option value="batsman">Batsman</option>
                      <option value="bowler">Bowler</option>
                      <option value="all-rounder">All-rounder</option>
                      <option value="wicket-keeper">Wicket-keeper</option>
                    </select>
                    <Filter className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <ChevronDown className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 overflow-x-auto">
          <div className="flex space-x-2 bg-white rounded-xl p-1 shadow-md">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'all' ? 'bg-violet-100 text-violet-900' : 'text-gray-500 hover:text-violet-700 hover:bg-gray-50'
              }`}
            >
              All Players
            </button>
            <button
              onClick={() => setActiveTab('byRole')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'byRole' ? 'bg-violet-100 text-violet-900' : 'text-gray-500 hover:text-violet-700 hover:bg-gray-50'
              }`}
            >
              By Role
            </button>
            <button
              onClick={() => setActiveTab('management')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'management' ? 'bg-violet-100 text-violet-900' : 'text-gray-500 hover:text-violet-700 hover:bg-gray-50'
              }`}
            >
              Team Management
            </button>
          </div>
        </div>

        {/* Dynamic Content based on active tab */}
        {activeTab === 'all' && (
          <div className="bg-white rounded-xl p-6 mb-6 shadow-md">
            <h3 className="text-xl font-bold text-violet-900 mb-5">Team Squad</h3>
            
            {filteredTeamMembers.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-500">No players found matching your criteria.</p>
                <button 
                  onClick={() => {setSearchTerm(''); setSelectedFilter('all');}}
                  className="mt-2 text-violet-600 hover:text-violet-800 text-sm font-medium"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
                {filteredTeamMembers.map(player => (
                  <div 
                    key={player.id}
                    className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-all hover:border-violet-200 group"
                    onClick={() => setSelectedPlayer(player)}
                  >
                    {/* Player Image */}
                    <div className="relative">
                      <img 
                        src={player.image || "/api/placeholder/300/300"} 
                        alt={player.name}
                        className="w-full h-48 object-cover object-center group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute top-2 right-2 bg-amber-400 w-8 h-8 rounded-full flex items-center justify-center text-violet-900 font-bold shadow-lg border-2 border-white">
                        {player.jersey}
                      </div>
                    </div>
                    
                    {/* Player Info */}
                    <div className="p-3">
                      <h3 className="text-lg font-bold text-violet-900 truncate">{player.name}</h3>
                      <div className="flex items-center space-x-1 mb-1">
                        <div className={`flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white ${getRoleBadgeColor(player.role)}`}>
                          {getRoleIcon(player.role)}
                          {player.role}
                        </div>
                      </div>
                      <p className="text-gray-600 text-xs">{player.specialization}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'byRole' && (
          <div className="space-y-6">
            {/* Batsmen */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center mr-3">
                  <User className="h-4 w-4 text-emerald-500" />
                </div>
                <h4 className="text-lg font-bold text-violet-900">Batsmen</h4>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {teamMembers.filter(player => player.role === "Batsman").map(player => (
                  <div 
                    key={player.id}
                    className="flex items-center bg-gray-50 hover:bg-emerald-50 rounded-xl p-3 cursor-pointer shadow-sm hover:shadow transition-all"
                    onClick={() => setSelectedPlayer(player)}
                  >
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-3 border-2 border-emerald-500 bg-white">
                      <img src={player.image || "/api/placeholder/100/100"} alt={player.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h5 className="text-violet-900 font-medium">{player.name}</h5>
                      <p className="text-gray-600 text-xs">{player.specialization}</p>
                    </div>
                    <div className="ml-auto bg-emerald-500 rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold text-white">
                      {player.jersey}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Bowlers */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center mr-3">
                  <Award className="h-4 w-4 text-amber-500" />
                </div>
                <h4 className="text-lg font-bold text-violet-900">Bowlers</h4>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {teamMembers.filter(player => player.role === "Bowler").map(player => (
                  <div 
                    key={player.id}
                    className="flex items-center bg-gray-50 hover:bg-amber-50 rounded-xl p-3 cursor-pointer shadow-sm hover:shadow transition-all"
                    onClick={() => setSelectedPlayer(player)}
                  >
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-3 border-2 border-amber-500 bg-white">
                      <img src={player.image || "/api/placeholder/100/100"} alt={player.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h5 className="text-violet-900 font-medium">{player.name}</h5>
                      <p className="text-gray-600 text-xs">{player.specialization}</p>
                    </div>
                    <div className="ml-auto bg-amber-500 rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold text-white">
                      {player.jersey}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* All-rounders */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center mr-3">
                  <Users className="h-4 w-4 text-violet-500" />
                </div>
                <h4 className="text-lg font-bold text-violet-900">All-rounders</h4>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {teamMembers.filter(player => player.role === "All-rounder").map(player => (
                  <div 
                    key={player.id}
                    className="flex items-center bg-gray-50 hover:bg-violet-50 rounded-xl p-3 cursor-pointer shadow-sm hover:shadow transition-all"
                    onClick={() => setSelectedPlayer(player)}
                  >
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-3 border-2 border-violet-500 bg-white">
                      <img src={player.image || "/api/placeholder/100/100"} alt={player.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h5 className="text-violet-900 font-medium">{player.name}</h5>
                      <p className="text-gray-600 text-xs">{player.specialization}</p>
                    </div>
                                   <div className="ml-auto bg-violet-500 rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold text-white">
                      {player.jersey}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'management' && (
          <div className="bg-white rounded-xl p-6 shadow-md text-center text-gray-500">
            <p>Team management content coming soon...</p>
          </div>
        )}

        {/* Modal for Player Details */}
        <Transition appear show={isModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {selectedPlayer?.name} (#{selectedPlayer?.jersey})
                    </Dialog.Title>
                    <div className="mt-4">
                      <img
                        src={selectedPlayer?.image || "/api/placeholder/300/300"}
                        alt={selectedPlayer?.name}
                        className="w-full h-64 object-cover rounded-xl mb-4"
                      />
                      <div className="space-y-2">
                        <p><strong>Role:</strong> {selectedPlayer?.role}</p>
                        <p><strong>Specialization:</strong> {selectedPlayer?.specialization}</p>
                        <p><strong>Country:</strong> {selectedPlayer?.country}</p>
                        <p><strong>Stats:</strong> {selectedPlayer?.stats}</p>
                      </div>
                    </div>

                    <div className="mt-6">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                        onClick={closeModal}
                      >
                        Close
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div> {/* End of main container */}
    </div>
  );
};

export default CricketTeamPortfolio;